import React, { useMemo, useEffect, useState } from 'react';

import { Typography } from '@mui/material';
import { Stack } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

import CardView from '../../../components/Redesing/card-view';
import { MovaticFieldGroupAsyncSelect } from '../../../components/Redesing/movatic-field-group';
import NoFilePlaceHolder from '../../../components/Redesing/no-file-place-holder';
import { MovaticFieldGroupText } from '../../../components/Redesing/movatic-field-group';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import FileUpload from '../../../components/Redesing/file-upload';
import CustomToast from '../../../components/Redesing/custom-toast';

import { Option } from '../../../ts/interface/components/table.interface';

import { history } from '../../../utils';

import useOrganizations from '../../../hooks/system/query/useOrganizations';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import useOrganization from '../../../hooks/use-organization';
import { useOrganizationDetailRelayQuery } from '../../../hooks/organization/query/V2';

interface IGeneralTabState {
  name: string;
  parentOrganization: Option | null;
  parentOrganizationValidationResult: null | string;
  parentOrganizationValidationHelp: string;
  nameValidationResult: null | string;
  nameValidationHelp: string;
  logoFiles: any[];
  buttonDisabled: boolean;
  saveButtonLoading: boolean;
}

function GeneralTab() {
  const organizationInfo = useOrganization();

  const initialState = {
    name: organizationInfo?.name || '',
    parentOrganization: null,
    parentOrganizationValidationResult: null,
    parentOrganizationValidationHelp: '',
    nameValidationResult: null,
    nameValidationHelp: '',
    logoFiles: [],
    buttonDisabled: true,
    saveButtonLoading: false,
  };

  const [
    {
      name,
      parentOrganization,
      parentOrganizationValidationResult,
      parentOrganizationValidationHelp,
      nameValidationResult,
      nameValidationHelp,
      logoFiles,
      buttonDisabled,
      saveButtonLoading,
    },
    setState,
  ] = useState<IGeneralTabState>(initialState);

  const [openAsyncSelect, setOpenAsyncSelect] = useState(false);

  const { data, isLoading, refetch: refetchOrganizations } = useOrganizations();

  const parentOrganizations = useMemo(() => data?.data || [], [data]);

  // Fetch this organization's own details (incl. its parent) via GraphQL so
  // the "Parent Organization" field reflects the value stored in the backend.
  const { data: organizationDetail } = useOrganizationDetailRelayQuery(
    organizationInfo?.id
  );

  useEffect(() => {
    if (openAsyncSelect) {
      refetchOrganizations();
    }
  }, [openAsyncSelect, refetchOrganizations]);

  // Hydrate the parent-organization select once the detail query resolves.
  useEffect(() => {
    const parent = organizationDetail?.parent;
    setState((prevState) => ({
      ...prevState,
      parentOrganization: parent
        ? { label: parent.name, value: parent.id }
        : null,
    }));
  }, [organizationDetail]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const eventType = event.target.type;
    const eventName = event.target.name;
    let value = eventType === 'checkbox' ? event.target.checked : event.target.value;
    if (eventType === 'text' || eventType === 'select-one') {
      setState((prevState) => ({
        ...prevState,
        [eventName.concat('ValidationResult')]: null,
        [eventName.concat('ValidationHelp')]: '',
      }));
    }
    setState((prevState) => ({
      ...prevState,
      [eventName]: value,
    }));
  };

  const checkValidation = () => {
    let valid = true;
    if (!name) {
      setState((prevState) => ({
        ...prevState,
        nameValidationResult: 'error',
        nameValidationHelp: 'Please add a system name.',
      }));
      valid = false;
    }
    return valid;
  };

  const submit = () => {
    if (checkValidation()) {
      const organization = {
        name: name.trim(),
        parentOrganization: parentOrganization?.value,
        logo: logoFiles[0],
      };

      CustomAsyncToast({
        promise: () => {
          console.log(organization);
          return Promise.resolve();
        }, //createOrganization(organization),
        loadingMessage: 'Creating Organization...',
        successMessage: () => 'Organization Created Successfully',
        errorMessage: 'Error Creating Organization.',
      }).then((r) => {
        if (r) {
          history.push(`/dashboard`);
        }
      });
    }
  };

  const handleLogoUpload = (files: File[], rejected: File[]) => {
    // Handle rejected files first
    if (rejected.length > 0) {
      CustomToast({
        type: 'error',
        message: 'Incorrect file type. Please try a different image.',
        duration: 5000,
      });
      return;
    }

    // If no files were uploaded, exit early
    if (files.length === 0) return;

    // Create preview URL for the first file
    const previewUrl = URL.createObjectURL(files[0]);

    // Validate image dimensions
    const image = new Image();
    image.src = previewUrl;

    image.onload = () => {
      if (image.width !== 200 || image.height !== 200) {
        URL.revokeObjectURL(previewUrl); // Clean up the URL
        CustomToast({
          type: 'error',
          message: 'Your image is not 200px by 200px, please reformat and try again',
          duration: 5000,
        });
        setState((prev) => ({ ...prev, logoFiles: [] }));
      } else {
        setState((prev) => ({
          ...prev,
          logoFiles: [{ ...files[0], preview: previewUrl }],
        }));
      }
    };

    image.onerror = () => {
      URL.revokeObjectURL(previewUrl);
      CustomToast({
        type: 'error',
        message: 'Failed to load image. Please try again.',
        duration: 5000,
      });
    };
  };

  const parentOrganizationOptions = [
    ...parentOrganizations.map((parent: { name: string; id: string }) => ({
      label: parent.name,
      value: parent.id,
    })),
  ] as Option[];

  return (
    <CardView
      title={'General Settings'}
      headerActions={
        <IconButtonMenu
          buttons={[
            {
              label: 'Save',
              startIcon: <SaveIcon />,
              onClick: submit,
              disabled: saveButtonLoading || buttonDisabled,
            },
          ]}
        />
      }
      content={
        <Stack px={2} pb={1} gap={2}>
          <MovaticFieldGroupText
            id="name"
            name="name"
            label="Organization Name"
            description="Required"
            hasError={nameValidationResult === 'error'}
            errorMessage={nameValidationHelp}
            value={name || organizationInfo?.name}
            onChange={handleChange}
          />

          <MovaticFieldGroupAsyncSelect
            isLoading={isLoading}
            id={'parent_organization'}
            label="Parent Organization"
            name={'parent_organization'}
            options={parentOrganizationOptions}
            value={parentOrganization}
            showClearButton
            onChange={(event: any, newValue: any) =>
              setState((prevState) => ({
                ...prevState,
                parentOrganization: newValue,
              }))
            }
            errorMessage={parentOrganizationValidationHelp}
            hasError={parentOrganizationValidationResult === 'error'}
            open={openAsyncSelect}
            onOpen={() => {
              setOpenAsyncSelect(true);
            }}
            onClose={() => {
              setOpenAsyncSelect(false);
            }}
            isOptionEqualToValue={(option: any, value: any) =>
              option.value === value.value
            }
            getOptionText={(option: any) => option.label}
            handleSearch={() => {}}
          />
          {/** add input for logo */}
          <Stack direction={'row'} spacing={2} justifyContent={'space-between'}>
            <Stack>
              <Typography variant={'subtitle1'}>Logo Preview</Typography>
              {logoFiles.length > 0 ? (
                <img alt="" className="logoRadius" src={logoFiles[0].preview} />
              ) : (
                <NoFilePlaceHolder />
              )}
            </Stack>

            <Stack sx={{ width: '58%' }}>
              <Typography variant={'subtitle1'}>Upload Organization Logo</Typography>
              <FileUpload
                caption={
                  'The file must be a .png, 200px wide, 200px high, and less than 1MB'
                }
                onDrop={handleLogoUpload}
              />
            </Stack>
          </Stack>
        </Stack>
      }
    />
  );
}

export default GeneralTab;
